import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormBlock, StepProps } from '@/widgets/ServiceFormTemplate';

import {
  ConditionView,
  FormInputPhone,
  FormSelect,
  FormTextInput,
  FormUploader,
  StepControlPanel,
  StepPreview,
} from '@/shared/ui';

import { Loader } from '@/shared/ui';

import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/api/queryKeys';

import AuthContext from '@/contexts/Auth/AuthContext';

import api from '@/api';

import { PersonalDataSchema } from '../../model/types';

export const PersonalData: React.FC<StepProps> = ({ currentRequestId, onStepDraftSave, next }) => {
  const requestId = currentRequestId ?? '';
  const { control, watch, setValue } = useFormContext<PersonalDataSchema>();
  const { data: categoryEntities = [], isLoading: areCategoryEntitiesLoading } = useQuery({
    queryKey: queryKeys.ars.authorTypes(),
    queryFn: () => api.ars.dictionary.authorTypes(),
    select: (res) => res.data,
  });

  const { userData } = useContext(AuthContext);
  const selectedCategory = watch('category');
  const userCategory = watch('userCategory');
  const lastName = watch('lastName');
  const firstName = watch('firstName');
  const middleName = watch('middleName');
  const email = watch('email');
  const phone = watch('phone');

  useEffect(() => {
    if (selectedCategory && !selectedCategory.confirmation_required && userCategory?.length) {
      const ids = userCategory.map((attach) => attach.uuid);
      api.services.deleteAttachments({ request_id: requestId, ids });
      setValue('userCategory', []);
    }

    if (userData?.name_last && !lastName) {
      setValue('lastName', userData.name_last);
    }
    if (userData?.name_first && !firstName) {
      setValue('firstName', userData.name_first);
    }
    if (userData?.name_middle && !middleName) {
      setValue('middleName', userData.name_middle);
    }
    if (userData?.contact_email && !email) {
      setValue('email', userData.contact_email);
    }
    if (userData?.contact_phone && !phone) {
      setValue('phone', userData.contact_phone);
    }
  }, []);

  return (
    <ConditionView if={!areCategoryEntitiesLoading} else={<Loader />}>
      <FormBlock title="Ваші особисті дані">
        <FormTextInput
          name="lastName"
          control={control}
          label="Прізвище"
          required
          placeholder="Введіть прізвище"
          maxLength={256}
          disabled={!!userData?.name_last}
        />
        <FormTextInput
          name="firstName"
          control={control}
          label="Імʼя"
          required
          placeholder="Введіть імʼя"
          maxLength={256}
          disabled={!!userData?.name_first}
        />
        <FormTextInput
          name="middleName"
          control={control}
          label="По батькові"
          placeholder="Введіть по батькові"
          maxLength={256}
          disabled={!!userData?.name_middle}
        />
        <FormTextInput
          name="email"
          control={control}
          label="Email"
          required
          placeholder="example@example.com"
          defaultValue={userData?.contact_email}
        />
        <FormInputPhone name="phone" control={control} label="Номер телефона" placeholder="+380 (__) ___ __ __" />
        <FormSelect
          label="Категорія автора звернення"
          control={control}
          name="category"
          options={
            categoryEntities?.map((category) => ({
              id: category.id,
              label: category.name,
              confirmation_required: category.confirmation_required,
            })) ?? []
          }
        />
        <ConditionView
          if={Boolean(
            categoryEntities &&
            categoryEntities.length > 0 &&
            categoryEntities?.find((category) => category.id === selectedCategory?.id)?.confirmation_required,
          )}
        >
          <FormUploader
            name="userCategory"
            maxCount={3}
            control={control}
            request_id={requestId}
            label="Додайте сканкопію (фотокопію) документа, що підтверджує категорію автора звернення"
            required
            onSaveDraft={onStepDraftSave}
          />
        </ConditionView>
      </FormBlock>
      <StepControlPanel next={next} />
    </ConditionView>
  );
};

export const PersonalDataPreview: React.FC = () => {
  const { getValues } = useFormContext<PersonalDataSchema>();
  const { lastName, firstName, middleName, email, phone, category, userCategory } = getValues();

  return (
    <StepPreview
      title="Ваші особисті дані"
      data={[
        ['Прізвище', lastName],
        ['Імʼя', firstName],
        ['По батькові', middleName],
        ['Email', email],
        ['Номер телефона', phone],
        ['Категорія автора звернення', category && category.label],
        [
          'Cканкопія (фотокопія) документа, що підтверджує категорію автора звернення',
          userCategory && userCategory.map((file) => file.file_name).join(', '),
        ],
      ]}
    />
  );
};
